import { type Orden, ordenarLista } from "../../lib/orden";

/**
 * Las dos reglas del listado de clientes que no se ven fallar.
 *
 * `imprenta` las tiene inline en `ClientList.tsx`; acá van aparte porque un
 * contacto equivocado en la fila o un orden que no ordena no rompen nada — se
 * leen mal y ya.
 */

/** Lo mínimo que estas reglas necesitan de un contacto. */
export interface ContactoDeFila {
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  esPredeterminado: boolean;
}

/** Y de un cliente. */
export interface ClienteDeFila {
  razonSocial: string;
  nombreFantasia: string;
  cuit: string | null;
  creadoEn: string;
  contactos?: readonly ContactoDeFila[];
}

/**
 * El contacto que representa al cliente en la fila.
 *
 * El predeterminado, y si no hay ninguno marcado, el primero — la regla de
 * `imprenta` (`find(c => c.is_default) || contactos[0]`). Es **a quien se le
 * manda el presupuesto**: mostrar otro haría que el listado y el detalle digan
 * cosas distintas sobre el mismo cliente.
 */
export function contactoPrincipal<T extends { esPredeterminado: boolean }>(
  contactos: readonly T[] | undefined,
): T | null {
  if (!contactos || contactos.length === 0) return null;
  return contactos.find((c) => c.esPredeterminado) ?? contactos[0] ?? null;
}

/** Cómo se compara cada columna ordenable. `null` va SIEMPRE al final. */
function valorDe(cliente: ClienteDeFila, campo: string): string | number | null {
  switch (campo) {
    case "razonSocial":
      return cliente.razonSocial;
    case "nombreFantasia":
      return cliente.nombreFantasia;
    case "cuit":
      return cliente.cuit;
    case "contacto":
      return contactoPrincipal(cliente.contactos)?.nombre ?? null;
    case "creadoEn":
      return new Date(cliente.creadoEn).getTime();
    default:
      return null;
  }
}

/**
 * Ordena una copia, nunca la lista original: la que llega es la del hook de
 * datos, y mutarla deja el orden pegado aunque después se lo quite.
 *
 * Lo ausente va **al final en las dos direcciones**. Tratarlo como cadena vacía
 * —que es lo que hace `imprenta`— pone los clientes sin CUIT encabezando la
 * lista ascendente, que es lo contrario de lo que se busca al ordenar por CUIT.
 */
export function ordenarClientes<T extends ClienteDeFila>(
  clientes: readonly T[],
  orden: Orden | null,
): T[] {
  return ordenarLista(clientes, orden, valorDe);
}
